import React from "react";
import api from "../../../api/api"; // adjust relative path

const Step2Personal = ({ job, form, handleChange, prevStep, nextStep }) => {
  const [canadaDocs, setCanadaDocs] = React.useState([]);
  const [usDocs, setUsDocs] = React.useState([]);
  const isDriver = job ? [2, 8, 11].includes(Number(job.p_category)) : false;
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchDocs = async (docType, setter) => {
    try {
      const endpoint =
        docType === "us"
          ? `/api/candidate/user/docs/${docType}`
          : `/api/candidate/user/docs-canada/${docType}`;
      const { data } = await api.get(endpoint, {
        headers: getAuthHeaders(),
      });
      setter(data);
    } catch (err) {
      console.error(`❌ Failed to fetch ${docType} docs:`, err);
    }
  };

  React.useEffect(() => {
    if (form.dq_legal_right_canada === "Yes" && form.canada_doc_type) {
      fetchDocs(form.canada_doc_type, setCanadaDocs);
    } else {
      setCanadaDocs([]);
    }
    if (form.dq_legal_right_us === "Yes") fetchDocs("us", setUsDocs);
    else setUsDocs([]);
  }, [
    form.canada_doc_type,
    form.dq_legal_right_canada,
    form.dq_legal_right_us,
  ]);

  const handleUpload = async (input, docType, setter) => {
    if (!input) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    const fd = new FormData();

    if (docType === "pr_card" || docType === "us") {
      Array.from(input).forEach((f) => {
        if (!allowedTypes.includes(f.type)) {
          alert("Only PDF, JPG, or PNG files are allowed");
          return;
        }
        fd.append("files", f); // MUST match backend
      });
    } else {
      if (!allowedTypes.includes(input.type)) {
        alert("Only PDF, JPG, or PNG files are allowed");
        return;
      }
      fd.append("file", input);
    }

    try {
      const authHeaders = getAuthHeaders();
      if (!authHeaders.Authorization) {
        alert("Session expired. Please log in again.");
        return;
      }

      const uploadEndpoint =
        docType === "us"
          ? `/api/candidate/user/upload-prove/us`
          : `/api/candidate/user/upload-canada/${docType}`;

      await api.post(uploadEndpoint, fd, {
        headers: {
          ...authHeaders,
          "Content-Type": "multipart/form-data",
        },
      });
      fetchDocs(docType, setter);
    } catch (err) {
      console.error("❌ Upload failed:", err);
      alert("❌ Upload failed");
    }
  };

  const handleDelete = async (docId, docType, setter) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      const endpoint =
        docType === "us"
          ? `/api/candidate/user/docs/${docId}`
          : `/api/candidate/user/docs-canada/${docType}/${docId}`;
      await api.delete(endpoint, {
        headers: getAuthHeaders(),
      });
      fetchDocs(docType, setter);
    } catch (err) {
      console.error("❌ Delete failed:", err);
      alert("❌ Delete failed");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (e.target.checkValidity()) {
      nextStep(); // only go forward if valid
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <fieldset className="border p-3 mb-4">
        <legend>Personal Information</legend>

        {/* Referral */}
        <div className="mb-3">
          <label className="d-block">
            Did someone refer you? <span className="text-danger">*</span>
          </label>
          <div className="btn-group w-100" role="group">
            <input
              type="radio"
              className="btn-check"
              name="refer_status"
              value="Yes"
              checked={form.refer_status === "Yes"}
              onChange={handleChange}
              id="refer_yes"
              required
            />
            <label className="btn btn-outline-primary px-5" htmlFor="refer_yes">
              Yes
            </label>

            <input
              type="radio"
              className="btn-check"
              name="refer_status"
              value="No"
              checked={form.refer_status === "No"}
              onChange={handleChange}
              id="refer_no"
            />
            <label
              className="btn btn-outline-secondary px-5"
              htmlFor="refer_no"
            >
              No
            </label>
          </div>

          {form.refer_status === "Yes" && (
            <div className="row mt-3">
              <div className="col-md-6">
                <input
                  type="text"
                  name="refer_by_name"
                  className="form-control mb-2"
                  placeholder="Employee Name"
                  value={form.refer_by_name}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6">
                <input
                  type="text"
                  name="refer_by_id"
                  className="form-control mb-2"
                  placeholder="Employee ID"
                  value={form.refer_by_id}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}
        </div>

        {job && isDriver && (
          <>
            {/* Legal right questions */}
            <div className="mb-3">
              <label className="d-block">
                Do you have the legal right to work in Canada?{" "}
                <span className="text-danger">*</span>
              </label>
              <div className="btn-group w-100" role="group">
                <input
                  type="radio"
                  className="btn-check"
                  name="dq_legal_right_canada"
                  value="Yes"
                  checked={form.dq_legal_right_canada === "Yes"}
                  onChange={handleChange}
                  id="canada_yes"
                  required
                />
                <label
                  className="btn btn-outline-primary px-5"
                  htmlFor="canada_yes"
                >
                  Yes
                </label>
                <input
                  type="radio"
                  className="btn-check"
                  name="dq_legal_right_canada"
                  value="No"
                  checked={form.dq_legal_right_canada === "No"}
                  onChange={handleChange}
                  id="canada_no"
                />
                <label
                  className="btn btn-outline-secondary px-5"
                  htmlFor="canada_no"
                >
                  No
                </label>
              </div>

              {form.dq_legal_right_canada === "Yes" && (
                <div className="mt-2">
                  <label>Upload proof (Canada) (Optional)</label>

                  {/* Dropdown to select document type */}
                  <div className="input-group mb-2">
                    <select
                      className="form-select"
                      value={form.canada_doc_type || ""}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: "canada_doc_type",
                            value: e.target.value,
                          },
                        })
                      }
                    >
                      <option value="">Select document type</option>
                      <option value="work_permit">Work Permit</option>
                      <option value="passport">Passport</option>
                      <option value="pr_card">PR Card</option>
                    </select>

                    <input
                      type="file"
                      multiple={form.canada_doc_type === "pr_card"}
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="form-control"
                      onChange={(e) => {
                        if (!form.canada_doc_type) {
                          alert("Please select a document type first");
                          return;
                        }

                        const files =
                          form.canada_doc_type === "pr_card"
                            ? e.target.files
                            : e.target.files?.[0];

                        handleUpload(
                          files,
                          form.canada_doc_type,
                          setCanadaDocs,
                        );
                      }}
                    />
                  </div>

                  {/* Uploaded files list */}
                  <ul className="list-group mt-2">
                    {canadaDocs.map((doc) => (
                      <li
                        key={doc.id || doc.doc_id}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        {doc.original || doc.doc_original}
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() =>
                            handleDelete(
                              doc.id || doc.doc_id,
                              doc.file?.includes("uploads/prcards")
                                ? "pr_card"
                                : form.canada_doc_type,
                              setCanadaDocs,
                            )
                          }
                        >
                          🗑
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="d-block">
                Do you have the legal right to enter the United States?{" "}
                <span className="text-danger">*</span>
              </label>
              <div className="btn-group w-100" role="group">
                <input
                  type="radio"
                  className="btn-check"
                  name="dq_legal_right_us"
                  value="Yes"
                  checked={form.dq_legal_right_us === "Yes"}
                  onChange={handleChange}
                  id="us_yes"
                  required
                />
                <label
                  className="btn btn-outline-primary px-5"
                  htmlFor="us_yes"
                >
                  Yes
                </label>
                <input
                  type="radio"
                  className="btn-check"
                  name="dq_legal_right_us"
                  value="No"
                  checked={form.dq_legal_right_us === "No"}
                  onChange={handleChange}
                  id="us_no"
                />
                <label
                  className="btn btn-outline-secondary px-5"
                  htmlFor="us_no"
                >
                  No
                </label>
              </div>

              {form.dq_legal_right_us === "Yes" && (
                <div className="mt-2">
                  <label>Upload proof (US) (Optional)</label>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="form-control"
                    onChange={(e) =>
                      handleUpload(e.target.files, "us", setUsDocs)
                    }
                  />

                  {/* Uploaded files list */}
                  <ul className="list-group mt-2">
                    {usDocs.map((doc) => (
                      <li
                        key={doc.doc_id}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        {doc.doc_original}
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() =>
                            handleDelete(doc.doc_id, "us", setUsDocs)
                          }
                        >
                          🗑
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="d-block">
                Have you ever worked for New Way Trucking Ltd before?{" "}
                <span className="text-danger">*</span>
              </label>
              <div className="btn-group w-100" role="group">
                <input
                  type="radio"
                  className="btn-check"
                  name="dq_previous_nwt_employee"
                  value="Yes"
                  checked={form.dq_previous_nwt_employee === "Yes"}
                  onChange={handleChange}
                  id="worked_yes"
                  required
                />
                <label
                  className="btn btn-outline-primary px-5"
                  htmlFor="worked_yes"
                >
                  Yes
                </label>
                <input
                  type="radio"
                  className="btn-check"
                  name="dq_previous_nwt_employee"
                  value="No"
                  checked={form.dq_previous_nwt_employee === "No"}
                  onChange={handleChange}
                  id="worked_no"
                />
                <label
                  className="btn btn-outline-secondary px-5"
                  htmlFor="worked_no"
                >
                  No
                </label>
              </div>

              {form.dq_previous_nwt_employee === "Yes" && (
                <div className="row mt-3">
                  <div className="col-md-6">
                    <label>
                      From Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      name="dq_previous_nwt_from"
                      className="form-control"
                      value={form.dq_previous_nwt_from || ""}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label>
                      To Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      name="dq_previous_nwt_to"
                      className="form-control"
                      value={form.dq_previous_nwt_to || ""}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <div className="d-flex justify-content-between">
          <button className="btn btn-secondary" onClick={prevStep}>
            ← Back
          </button>
          <button type="submit" className="btn btn-primary">
            Next →
          </button>
        </div>
      </fieldset>
    </form>
  );
};

export default Step2Personal;
